import { useCallback, useEffect, useRef, useState } from 'react'
import { ExternalLink, Eye, EyeOff, Pause, Play, Volume2, VolumeX } from 'lucide-react'

type HeroSlide = {
  label: string
  eyebrow: string
  poster: string
  local?: boolean
  vimeoId?: string
  url?: string
}

type NetworkInformation = {
  downlink?: number
  effectiveType?: string
  saveData?: boolean
}

type NavigatorProfile = Navigator & {
  connection?: NetworkInformation
  deviceMemory?: number
}

const SLIDES: HeroSlide[] = [
  {
    label: '3 Persimmon Lane',
    eyebrow: 'Harbeson Waterfront',
    poster: '/media/hero/3-persimmon-lane.jpg',
    vimeoId: '1193076094',
    url: 'https://vimeo.com/1193076094',
  },
  {
    label: 'Coastal Delaware',
    eyebrow: 'Our Coast',
    poster: '/media/hero/drone-clips-poster.jpg',
    local: true,
    url: 'https://pub-fabb5cd90faf4ea58da21e10c156308d.r2.dev/coastal-delaware/drone-clips-full.mp4',
  },
  {
    label: '40 Beach Avenue',
    eyebrow: 'Rehoboth Beach',
    poster: '/media/hero/40-beach-avenue.jpg',
    vimeoId: '1027985698',
    url: 'https://vimeo.com/1027985698',
  },
  {
    label: '54 Eleanor Lee',
    eyebrow: 'Coastal Home Tour',
    poster: '/media/hero/54-eleanor-lee.jpg',
    vimeoId: '1011757950',
    url: 'https://vimeo.com/1011757950',
  },
]

const LOOP_START_SECONDS = 0.7
const LOOP_END_PADDING_SECONDS = 0.8
const COASTAL_VIDEO = {
  high: 'https://pub-fabb5cd90faf4ea58da21e10c156308d.r2.dev/coastal-delaware/drone-clips-hero-60s.mp4',
  balanced: 'https://pub-fabb5cd90faf4ea58da21e10c156308d.r2.dev/coastal-delaware/drone-clips-hero-60s-720p.mp4',
  lite: 'https://pub-fabb5cd90faf4ea58da21e10c156308d.r2.dev/coastal-delaware/drone-clips-hero-60s-540p.mp4',
} as const
const HERO_DETAILS_CHANGE_EVENT = 'debeach:hero-details-change'
const SHOW_HERO_DETAILS_EVENT = 'debeach:show-hero-details'

const CoastalPortrait = ({ className, imageClassName = 'object-top' }: { className: string; imageClassName?: string }) => (
  <div
    role="img"
    aria-label="Merritt Cooch and Shawn McDonnell together at the Delaware coast"
    data-portrait-shape="editorial-frame"
    className={className}
  >
    <span className="pointer-events-none absolute -left-2 -top-2 h-16 w-16 border-l border-t border-accent-gold" aria-hidden="true" />
    <div className="relative h-full w-full overflow-hidden border border-white/65 bg-primary-ink/30 shadow-[0_28px_70px_rgba(6,20,31,0.4)] backdrop-blur-sm">
      <img src="/media/team/merritt-shawn-coastal-portrait.webp" alt="" width={1000} height={1300} className={`h-full w-full object-cover ${imageClassName}`} />
      <span className="absolute inset-0 bg-gradient-to-t from-primary-ink/75 via-transparent to-white/5" aria-hidden="true" />
      <span className="absolute inset-x-0 bottom-0 border-t border-white/25 bg-primary-ink/35 px-4 py-3 text-left backdrop-blur-md">
        <span className="block font-serif text-lg italic leading-none text-white">Merritt &amp; Shawn</span>
        <span className="mt-1.5 block font-sans text-[7px] font-medium uppercase tracking-[0.2em] text-white/65">Rehoboth Beach · Delaware</span>
      </span>
    </div>
    <span className="absolute -bottom-3 -right-3 flex h-12 w-12 items-center justify-center bg-accent-gold font-serif text-sm italic text-primary-ink shadow-xl" aria-hidden="true">M+S</span>
  </div>
)

const HeroShowcase = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const localVideoRef = useRef<HTMLVideoElement>(null)
  const desktopIdentityRef = useRef<HTMLDivElement>(null)
  const toolbarMotionTimerRef = useRef<number | null>(null)
  const bufferingTimerRef = useRef<number | null>(null)
  const autoAdvancingRef = useRef(false)
  const playbackActiveRef = useRef(false)
  const resumeAfterVisibilityRef = useRef(false)
  const [active, setActive] = useState(0)
  const [playRequested, setPlayRequested] = useState(false)
  const [mediaReady, setMediaReady] = useState(false)
  const [soundOn, setSoundOn] = useState(false)
  const [detailsVisible, setDetailsVisible] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [localVideoSource, setLocalVideoSource] = useState<string>(COASTAL_VIDEO.balanced)
  const [localVideoTier, setLocalVideoTier] = useState<keyof typeof COASTAL_VIDEO>('balanced')
  const [identityVisible, setIdentityVisible] = useState(false)
  const [mobileViewport, setMobileViewport] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)
  const [toolbarMotion, setToolbarMotion] = useState<'idle' | 'collapsing' | 'expanding'>('idle')
  const slide = SLIDES[active]

  const clearBufferingTimer = useCallback(() => {
    if (bufferingTimerRef.current !== null) window.clearTimeout(bufferingTimerRef.current)
    bufferingTimerRef.current = null
  }, [])

  const deferBufferFallback = useCallback(() => {
    clearBufferingTimer()
    bufferingTimerRef.current = window.setTimeout(() => {
      bufferingTimerRef.current = null
      setMediaReady(false)
    }, 180)
  }, [clearBufferingTimer])

  const revealMediaAfterDecode = useCallback(() => {
    clearBufferingTimer()
    bufferingTimerRef.current = window.setTimeout(() => {
      bufferingTimerRef.current = null
      setMediaReady(true)
    }, 180)
  }, [clearBufferingTimer])

  const prepareSlideChange = useCallback(() => {
    clearBufferingTimer()
    playbackActiveRef.current = false
    resumeAfterVisibilityRef.current = false
    localVideoRef.current?.pause()
    iframeRef.current?.contentWindow?.postMessage({ method: 'pause' }, 'https://player.vimeo.com')
    setPlayRequested(true)
    setMediaReady(false)
    setSoundOn(false)
    setIsPlaying(false)
  }, [clearBufferingTimer])

  const advanceSlide = useCallback(() => {
    if (autoAdvancingRef.current) return
    autoAdvancingRef.current = true
    prepareSlideChange()
    setActive((current) => (current + 1) % SLIDES.length)
  }, [prepareSlideChange])

  const chooseSlide = useCallback((index: number) => {
    autoAdvancingRef.current = false
    prepareSlideChange()
    setActive(index)
  }, [prepareSlideChange])

  useEffect(() => {
    autoAdvancingRef.current = false
  }, [active])

  useEffect(() => {
    return () => {
      clearBufferingTimer()
      if (toolbarMotionTimerRef.current !== null) window.clearTimeout(toolbarMotionTimerRef.current)
    }
  }, [clearBufferingTimer])

  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)')
    const updateViewport = () => setMobileViewport(query.matches)
    query.addEventListener('change', updateViewport)
    return () => query.removeEventListener('change', updateViewport)
  }, [])

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) {
      setIdentityVisible(true)
      return
    }

    const revealFrame = window.requestAnimationFrame(() => setIdentityVisible(true))
    let scrollFrame = 0
    const updateIdentity = () => {
      scrollFrame = 0
      const progress = Math.min(1, window.scrollY / (window.innerHeight * 0.7))
      if (desktopIdentityRef.current) {
        desktopIdentityRef.current.style.transform = `translate3d(0, ${Math.round(progress * 24)}px, 0)`
        desktopIdentityRef.current.style.opacity = String(1 - progress * 0.7)
      }
    }
    const onScroll = () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateIdentity)
    }
    updateIdentity()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.cancelAnimationFrame(revealFrame)
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    const profile = navigator as NavigatorProfile
    const connection = profile.connection
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const compactViewport = window.innerWidth <= 768
    const limitedDevice = (profile.deviceMemory ?? 8) <= 4 || (profile.hardwareConcurrency ?? 8) <= 4
    const constrainedNetwork = connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g' || connection?.effectiveType === '3g' || (connection?.downlink !== undefined && connection.downlink < 5)
    const tier: keyof typeof COASTAL_VIDEO = compactViewport
      ? limitedDevice || constrainedNetwork ? 'lite' : 'balanced'
      : constrainedNetwork ? 'balanced' : 'high'
    setLocalVideoTier(tier)
    setLocalVideoSource(COASTAL_VIDEO[tier])
    if (navigator.webdriver || reducedMotion || connection?.saveData || connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g') return
    setPlayRequested(true)
  }, [])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        resumeAfterVisibilityRef.current = playbackActiveRef.current
        localVideoRef.current?.pause()
        iframeRef.current?.contentWindow?.postMessage({ method: 'pause' }, 'https://player.vimeo.com')
        return
      }
      if (!resumeAfterVisibilityRef.current) return
      resumeAfterVisibilityRef.current = false
      if (localVideoRef.current) localVideoRef.current.play().catch(() => {})
      else iframeRef.current?.contentWindow?.postMessage({ method: 'play' }, 'https://player.vimeo.com')
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://player.vimeo.com' || event.source !== iframeRef.current?.contentWindow) return
      let data: { event?: string }
      if (typeof event.data === 'string') {
        try {
          data = JSON.parse(event.data) as { event?: string }
        } catch {
          return
        }
      } else data = event.data as { event?: string }
      const player = iframeRef.current?.contentWindow
      if (data?.event === 'ready') {
        for (const value of ['play', 'pause', 'ended']) {
          player?.postMessage({ method: 'addEventListener', value }, 'https://player.vimeo.com')
        }
        player?.postMessage(
          { method: 'setVolume', value: soundOn ? 1 : 0 },
          'https://player.vimeo.com',
        )
      }
      if (data?.event === 'play') {
        autoAdvancingRef.current = false
        playbackActiveRef.current = true
        setIsPlaying(true)
        revealMediaAfterDecode()
      }
      if (data?.event === 'pause') {
        clearBufferingTimer()
        playbackActiveRef.current = false
        setIsPlaying(false)
      }
      if (data?.event === 'ended') {
        clearBufferingTimer()
        playbackActiveRef.current = false
        setIsPlaying(false)
        advanceSlide()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [active, advanceSlide, clearBufferingTimer, revealMediaAfterDecode, soundOn])

  const requestPlayback = () => {
    if (!playRequested) {
      playbackActiveRef.current = true
      setIsPlaying(true)
      setMediaReady(false)
      setPlayRequested(true)
      setSoundOn(Boolean(slide.vimeoId))
      return
    }
    if (slide.local) {
      const video = localVideoRef.current
      if (!video) return
      if (isPlaying) video.pause()
      else {
        if (video.duration - video.currentTime <= LOOP_END_PADDING_SECONDS) video.currentTime = LOOP_START_SECONDS
        video.play().catch(() => {})
      }
      return
    }
    const nextPlaying = !isPlaying
    playbackActiveRef.current = nextPlaying
    setIsPlaying(nextPlaying)
    iframeRef.current?.contentWindow?.postMessage(
      { method: nextPlaying ? 'play' : 'pause' },
      'https://player.vimeo.com',
    )
  }

  const toggleSound = () => {
    if (!slide.vimeoId) return
    if (!playRequested) {
      playbackActiveRef.current = true
      setIsPlaying(true)
      setMediaReady(false)
      setSoundOn(true)
      setPlayRequested(true)
      return
    }
    const next = !soundOn
    iframeRef.current?.contentWindow?.postMessage(
      { method: 'setVolume', value: next ? 1 : 0 },
      'https://player.vimeo.com',
    )
    if (next) {
      playbackActiveRef.current = true
      setIsPlaying(true)
      iframeRef.current?.contentWindow?.postMessage({ method: 'play' }, 'https://player.vimeo.com')
    }
    setSoundOn(next)
  }

  const hideDetails = useCallback(() => {
    if (toolbarMotion !== 'idle') return
    setToolbarMotion('collapsing')
    toolbarMotionTimerRef.current = window.setTimeout(() => {
      setDetailsVisible(false)
      setToolbarMotion('idle')
      toolbarMotionTimerRef.current = null
    }, 560)
  }, [toolbarMotion])

  const showDetails = useCallback(() => {
    if (toolbarMotionTimerRef.current !== null) window.clearTimeout(toolbarMotionTimerRef.current)
    setToolbarMotion('expanding')
    setDetailsVisible(true)
    toolbarMotionTimerRef.current = window.setTimeout(() => {
      setToolbarMotion('idle')
      toolbarMotionTimerRef.current = null
    }, 40)
  }, [])

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(HERO_DETAILS_CHANGE_EVENT, { detail: { visible: detailsVisible } }))
  }, [detailsVisible])

  useEffect(() => {
    const handleShowDetails = () => showDetails()
    window.addEventListener(SHOW_HERO_DETAILS_EVENT, handleShowDetails)
    return () => window.removeEventListener(SHOW_HERO_DETAILS_EVENT, handleShowDetails)
  }, [showDetails])

  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent(HERO_DETAILS_CHANGE_EVENT, { detail: { visible: true } }))
  }, [])

  const toolbarMotionClass = toolbarMotion === 'idle'
    ? 'translate-y-0 scale-100 opacity-100 blur-0'
    : '-translate-y-[65vh] scale-[0.38] opacity-0 blur-[2px]'

  const supportingDetailClass = toolbarMotion === 'idle'
    ? 'scale-100 opacity-100'
    : 'scale-95 opacity-0'

  const playbackLabel = isPlaying
    ? 'Pause Video'
    : playRequested
      ? 'Play Video'
      : 'Play Selected Video'

  const playbackIcon = isPlaying
    ? <Pause size={14} fill="currentColor" aria-hidden="true" />
    : <Play size={14} fill="currentColor" aria-hidden="true" />

  return (
    <>
      <div className="absolute inset-0 z-[1] overflow-hidden">
        <img
          src={slide.poster}
          srcSet={slide.local ? '/media/hero/drone-clips-poster.jpg 1920w' : undefined}
          sizes="100vw"
          alt=""
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover"
        />

        {slide.local && playRequested && (
          <video
            key={localVideoSource}
            ref={localVideoRef}
            data-video-tier={localVideoTier}
            width={1920}
            height={1080}
            autoPlay
            muted
            playsInline
            preload="auto"
            poster="/media/hero/drone-clips-poster.jpg"
            onLoadedMetadata={(event) => {
              event.currentTarget.currentTime = LOOP_START_SECONDS
            }}
            onCanPlay={(event) => {
              if (event.currentTarget.paused) event.currentTarget.play().catch(() => setIsPlaying(false))
            }}
            onPlay={() => {
              playbackActiveRef.current = true
              setIsPlaying(true)
            }}
            onPlaying={() => {
              autoAdvancingRef.current = false
              revealMediaAfterDecode()
            }}
            onWaiting={deferBufferFallback}
            onStalled={deferBufferFallback}
            onPause={() => {
              clearBufferingTimer()
              playbackActiveRef.current = false
              setIsPlaying(false)
            }}
            onError={() => {
              clearBufferingTimer()
              setMediaReady(false)
              setIsPlaying(false)
            }}
            onTimeUpdate={(event) => {
              const video = event.currentTarget
              if (video.duration - video.currentTime <= LOOP_END_PADDING_SECONDS) advanceSlide()
            }}
            onEnded={advanceSlide}
            className={`absolute inset-0 h-full w-full scale-[1.04] transform-gpu object-cover transition-opacity duration-700 will-change-[opacity] md:scale-100 ${mediaReady ? 'opacity-100' : 'opacity-0'}`}
          >
            <source src={localVideoSource} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}

        {slide.vimeoId && playRequested && (
          <iframe
            key={slide.vimeoId}
            ref={iframeRef}
            src={`https://player.vimeo.com/video/${slide.vimeoId}?autoplay=1&muted=1&controls=${mobileViewport ? 0 : 1}&title=0&byline=0&portrait=0&dnt=1&playsinline=1`}
            title={`${slide.label} hero video`}
            allow="autoplay; fullscreen; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className={`absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2 border-0 transition-opacity duration-500 ${mediaReady ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
          />
        )}

        {!playRequested && (
          <button
            type="button"
            data-touch-accent
            onClick={requestPlayback}
            aria-label={`Play ${slide.label}`}
            className="absolute left-[25%] top-[42%] z-10 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 md:left-1/2 items-center justify-center rounded-full border border-white/60 bg-white/90 text-primary-ink shadow-2xl backdrop-blur-md transition-all hover:scale-110 hover:bg-accent-gold md:h-20 md:w-20"
          >
            <Play size={26} fill="currentColor" aria-hidden="true" />
          </button>
        )}

      </div>

      {detailsVisible && (
        <div data-mobile-hero className="absolute inset-0 z-20 flex flex-col justify-end md:hidden">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-primary-ink/85" aria-hidden="true" />
          <button
            data-mobile-hero-portrait-toggle
            type="button"
            onClick={hideDetails}
            aria-label="Hide hero details"
            className={`absolute right-4 top-[7.5rem] h-[30svh] max-h-[270px] min-h-[210px] w-[43vw] max-w-[190px] overflow-visible text-left drop-shadow-[0_22px_28px_rgba(6,20,31,0.38)] transition-all duration-500 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-gold motion-reduce:transition-none ${supportingDetailClass}`}
          >
            <CoastalPortrait className="relative h-full w-full overflow-visible" imageClassName="scale-[1.28] object-[50%_28%]" />
          </button>

          <div className={`relative origin-top px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${toolbarMotionClass}`}>
            <div className="mb-3 flex items-center gap-2.5 text-white/75">
              <span className="h-px w-6 bg-accent-gold" aria-hidden="true" />
              <p className="font-sans text-[8px] font-medium uppercase tracking-[0.25em]">Coastal Delaware Real Estate</p>
            </div>
            <h1 className="font-sans text-[clamp(2.2rem,10.5vw,2.8rem)] font-medium leading-[0.86] tracking-[-0.06em] text-white">Merritt <span className="font-light text-white/60">&amp;</span> Shawn</h1>
            <p className="mt-2 max-w-[20rem] text-[11px] font-light leading-[1.45] text-white/75">Deep Delaware roots. Construction expertise. Personal guidance from Rehoboth Beach to the coast.</p>

            <div data-mobile-controls className="mt-3 flex items-center gap-2 border-t border-white/30 pt-3 text-white">
              <a href="#listings" data-touch-accent className="inline-flex h-11 flex-1 items-center justify-center bg-accent-gold px-3 font-sans text-[8px] font-medium uppercase tracking-[0.13em] text-primary-ink">Browse Homes</a>
              <a href="/sell" className="inline-flex h-11 flex-1 items-center justify-center border border-white/35 bg-primary-ink/35 px-3 font-sans text-[8px] font-medium uppercase tracking-[0.13em] text-white backdrop-blur">Sell With Us</a>
              <button type="button" disabled={!slide.vimeoId} onClick={toggleSound} aria-pressed={soundOn} aria-label={soundOn ? 'Turn sound off' : slide.vimeoId ? 'Turn sound on' : 'No audio'} className="inline-flex h-11 w-11 shrink-0 items-center justify-center border border-white/35 bg-primary-ink/35 text-white backdrop-blur transition-transform active:scale-90 disabled:opacity-35 motion-reduce:transition-none">
                {soundOn ? <Volume2 size={15} aria-hidden="true" /> : <VolumeX size={15} aria-hidden="true" />}
              </button>
              <button type="button" data-touch-accent onClick={requestPlayback} aria-pressed={isPlaying} aria-label={isPlaying ? 'Pause film' : 'Play film'} className="inline-flex h-11 w-11 shrink-0 items-center justify-center bg-white text-primary-ink">
                {playbackIcon}
              </button>
              <button type="button" onClick={hideDetails} aria-expanded={detailsVisible} aria-label="Hide Details" className="inline-flex h-11 w-11 shrink-0 items-center justify-center border border-white/35 bg-primary-ink/35 text-white backdrop-blur">
                <EyeOff size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsVisible && (
        <div data-desktop-hero className="absolute inset-x-0 bottom-0 z-20 hidden px-6 pb-6 md:block">
          <div className="mx-auto hidden w-full min-w-0 max-w-7xl items-end gap-5 md:grid md:grid-cols-[290px_minmax(0,1fr)] lg:grid-cols-[390px_minmax(0,1fr)] lg:gap-7">
            <CoastalPortrait
              className={`relative h-[min(56vh,560px)] min-h-[390px] w-full overflow-visible drop-shadow-[0_24px_30px_rgba(6,20,31,0.38)] transition-all duration-500 ease-out motion-reduce:transition-none ${supportingDetailClass}`}
            />

            <div className={`min-w-0 origin-top transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${toolbarMotionClass}`}>
              <div ref={desktopIdentityRef} className="mb-4 max-w-3xl will-change-transform">
                <div className={`transition-all duration-1000 ease-out motion-reduce:transition-none ${identityVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
                  <div className="flex items-center gap-3 text-white/80 [text-shadow:0_2px_18px_rgba(6,20,31,0.55)]">
                    <span className="h-px w-10 bg-accent-gold" aria-hidden="true" />
                    <p className="font-sans text-[9px] font-medium uppercase tracking-[0.3em]">Coastal Delaware Real Estate</p>
                  </div>
                  <h1 className="mt-2 whitespace-nowrap font-sans text-[clamp(3.35rem,5.2vw,5.75rem)] font-medium leading-[0.82] tracking-[-0.07em] text-white [text-shadow:0_8px_35px_rgba(6,20,31,0.45)]">
                    Merritt <span className="font-light text-white/55">&amp;</span> Shawn
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-white/80 [text-shadow:0_2px_16px_rgba(6,20,31,0.7)]">
                    Deep Delaware roots. Construction expertise. Personal guidance from Rehoboth Beach to the coast.
                  </p>
                </div>
              </div>

              <div data-hero-toolbar className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-[1.2rem] border border-white/25 bg-primary-ink/65 p-2 text-white shadow-[0_20px_60px_rgba(6,20,31,0.28)] backdrop-blur-xl">
                <div className="flex shrink-0 items-center gap-1.5">
                  <a href="#listings" data-touch-accent className="inline-flex h-9 items-center rounded-full bg-accent-gold px-3 font-sans text-[8px] font-medium uppercase tracking-[0.13em] text-primary-ink transition-colors hover:bg-white">Browse Homes</a>
                  <a href="/sell" className="hidden h-9 items-center rounded-full border border-white/20 bg-white/[0.07] px-3 font-sans text-[8px] font-medium uppercase tracking-[0.13em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold lg:inline-flex">Sell With Us</a>
                </div>

                <div className="mx-auto grid w-full max-w-[29rem] min-w-0 grid-cols-4 gap-1" role="tablist" aria-label="Hero videos">
                  {SLIDES.map((item, index) => (
                    <button
                      key={item.label}
                      type="button"
                      role="tab"
                      aria-label={`${item.eyebrow}: ${item.label}`}
                      aria-selected={active === index}
                      onClick={() => chooseSlide(index)}
                      className={`group inline-flex h-7 min-w-0 items-center justify-center gap-1 rounded-full border px-1.5 font-sans text-[9px] font-medium transition-colors ${active === index ? 'border-accent-gold bg-accent-gold text-primary-ink' : 'border-white/15 bg-white/[0.06] text-white/70 hover:border-white/35 hover:text-white'}`}
                    >
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${active === index ? 'bg-primary-ink' : 'bg-white/35 group-hover:bg-white'}`} aria-hidden="true" />
                      <span className="min-w-0 truncate"><span className="xl:hidden">{item.local ? 'C' : item.label.split(' ')[0]}</span><span className="hidden xl:inline">{item.label}</span></span>
                    </button>
                  ))}
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="sr-only" aria-live="polite">Selected {slide.label}</span>
                  <button type="button" data-touch-accent onClick={requestPlayback} aria-pressed={isPlaying} aria-label={isPlaying ? 'Pause film' : 'Play film'} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-primary-ink transition-colors hover:bg-accent-gold">
                    {playbackIcon}
                  </button>
                  <button type="button" disabled={!slide.vimeoId} onClick={toggleSound} aria-pressed={soundOn} aria-label={soundOn ? 'Turn sound off' : slide.vimeoId ? 'Turn sound on' : 'No audio'} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/[0.06] text-white/70 transition-colors hover:border-accent-gold hover:text-accent-gold disabled:cursor-not-allowed disabled:opacity-35">
                    {soundOn ? <Volume2 size={13} aria-hidden="true" /> : <VolumeX size={13} aria-hidden="true" />}
                  </button>
                  <button type="button" onClick={hideDetails} aria-expanded={detailsVisible} aria-label="Hide Details" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/[0.06] text-white/70 transition-colors hover:border-accent-gold hover:text-accent-gold">
                    <EyeOff size={13} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!detailsVisible && (
        <div className="hidden md:block absolute left-14 right-3 top-20 z-20 [animation:megamenu-item-in_500ms_cubic-bezier(0.22,1,0.36,1)_both] motion-reduce:animate-none md:inset-x-0 md:px-6">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 rounded-2xl border border-white/20 bg-primary-ink/90 px-3 py-3 text-white shadow-2xl backdrop-blur-xl md:px-5">
            <button type="button" onClick={showDetails} aria-expanded={detailsVisible} className="inline-flex h-10 items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 font-sans text-[9px] font-medium uppercase tracking-[0.14em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold">
              <Eye size={14} aria-hidden="true" /> Show Details
            </button>

            <div className="flex min-w-0 flex-1 items-center justify-center gap-3">
              <span className="hidden truncate font-sans text-[10px] font-medium uppercase tracking-[0.12em] text-white/75 sm:block">{slide.label}</span>
              <div className="flex items-center gap-2" role="tablist" aria-label="Hero videos">
                {SLIDES.map((item, index) => (
                  <button
                    key={item.label}
                    type="button"
                    role="tab"
                    aria-label={`Show ${item.label}`}
                    aria-selected={active === index}
                    onClick={() => chooseSlide(index)}
                    className={`h-3 w-3 rounded-full border transition-all ${active === index ? 'scale-125 border-accent-gold bg-accent-gold' : 'border-white/50 bg-white/20 hover:bg-white/60'}`}
                  />
                ))}
              </div>
            </div>

            <button type="button" disabled={!slide.vimeoId} onClick={toggleSound} aria-pressed={soundOn} aria-label={soundOn ? 'Turn sound off' : slide.vimeoId ? 'Turn sound on' : 'No audio'} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white transition-all hover:border-accent-gold hover:text-accent-gold active:scale-90 disabled:cursor-not-allowed disabled:opacity-45 motion-reduce:transition-none sm:hidden">
              {soundOn ? <Volume2 size={15} aria-hidden="true" /> : <VolumeX size={15} aria-hidden="true" />}
            </button>

            <div className="hidden items-center gap-2 sm:flex">
              {slide.url && (
                <a href={slide.url} target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1.5 font-sans text-[9px] font-medium uppercase tracking-[0.14em] text-white/75 transition-colors hover:text-accent-gold sm:inline-flex">Full tour <ExternalLink size={12} aria-hidden="true" /></a>
              )}
              <button type="button" onClick={requestPlayback} aria-pressed={isPlaying} className="inline-flex h-9 items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 font-sans text-[9px] font-medium uppercase tracking-[0.14em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold">
                {playbackIcon} {playbackLabel}
              </button>
              <button type="button" disabled={!slide.vimeoId} onClick={toggleSound} aria-pressed={soundOn} className="inline-flex h-9 items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 font-sans text-[9px] font-medium uppercase tracking-[0.14em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold disabled:cursor-not-allowed disabled:opacity-45">
                {soundOn ? <Volume2 size={14} aria-hidden="true" /> : <VolumeX size={14} aria-hidden="true" />}
                {soundOn ? 'Sound On' : slide.vimeoId ? 'Turn Sound On' : 'No Audio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HeroShowcase
